import { ReactNode } from 'react';
import { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { Hooks } from '@wellstone-solutions/common';
import { Form as MuiForm, Stack } from '@wellstone-solutions/web';
import { AxiosError, AxiosResponse } from 'axios';
import { AnyObjectSchema } from 'yup';

import { Button } from '@components/ui';
import { FORM_BUTTON_LABELS } from '@modules/auth/constants';

type FormikFormType = ReturnType<typeof Hooks.useForm<Record<string, unknown>>>;

interface FormProps<TData, TVariables> {
  children: (form: FormikFormType) => ReactNode;
  formId: string;
  initialValues: Record<string, unknown>;
  message?: string;
  mutateOptions: UseMutationOptions<
    AxiosResponse<TData>,
    AxiosError<{ [key: string]: string[] }>,
    TVariables,
    unknown
  >;
  mutation: UseMutationResult<
    AxiosResponse<TData>,
    AxiosError<{ [key: string]: string[] }>,
    TVariables
  >;
  schema: AnyObjectSchema;
}

const Form = <TData, TVariables>({
  children,
  formId,
  initialValues,
  message,
  mutateOptions,
  mutation,
  schema,
}: FormProps<TData, TVariables>) => {
  const { mutate, isPending, isSuccess } = mutation;

  const formik = Hooks.useForm({
    schema: schema,
    validationEngine: 'yup',
    initialValues: initialValues,
    onSubmit: (values) => {
      if (isPending || isSuccess) return;

      mutate(values as TVariables, {
        onError: (error) => {
          if (!error?.response?.data) return;

          // Attempt to map field errors from the server to the form
          Object.entries(error.response.data).forEach(([key, values]) => {
            formik.setFieldError(key, values.join(' '));
          });
        },
        onSettled: () => {
          // Resets the form button state
          formik.setSubmitting(false);
        },
        ...mutateOptions,
      });
    },
  });

  return (
    <MuiForm onSubmit={formik.handleSubmit} name={formId} id={formId}>
      <Stack spacing={2} sx={{ alignItems: 'center' }}>
        {message && <p>{message}</p>}
        {children(formik)}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}>
          {FORM_BUTTON_LABELS[formId] ?? 'Save'}
        </Button>
      </Stack>
    </MuiForm>
  );
};

export { Form };
